import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  archiveForm,
  duplicateForm,
  saveFormDetails,
  unarchiveForm,
} from "@/api/form-builder/formBuilder.mutations";
import { FormBuilderQueryKeys } from "@/api/form-builder/formBuilder.queries";
import {
  isFormNameTaken,
  FORM_NAME_TAKEN_FALLBACK_MESSAGE,
} from "@/api/form-builder/formNameTaken";
import { handleApiError, handleApiSuccess, showErrorAlert } from "@/utils/responseHandler";
import type { ApiError } from "@/types/common.types";

const useFormListMutation = <TVariables, TData>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  successMessage: string,
  { nameTakenShownInline = false } = {},
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      handleApiSuccess(successMessage);
      queryClient.invalidateQueries({ queryKey: [FormBuilderQueryKeys.list] });
      queryClient.invalidateQueries({ queryKey: [FormBuilderQueryKeys.detail] });
    },
    onError: (error) => {
      if (isFormNameTaken(error)) {
        if (!nameTakenShownInline) {
          const apiError = error as ApiError;
          showErrorAlert(
            apiError.response?.data?.error?.message ?? FORM_NAME_TAKEN_FALLBACK_MESSAGE,
            "Name already in use",
          );
        }
        return;
      }
      handleApiError(error as ApiError);
    },
  });
};

export const useArchiveForm = () => useFormListMutation(archiveForm, "Form archived");

export const useUnarchiveForm = () => useFormListMutation(unarchiveForm, "Form restored");

export const useDuplicateForm = () => useFormListMutation(duplicateForm, "Form duplicated");

export const useSaveFormDetails = () =>
  useFormListMutation(saveFormDetails, "Form details saved", { nameTakenShownInline: true });
